import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { useSwatches } from 'dibs-swatch-selector/exports/useSwatches';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { type OnClick } from '../constants';

import dibsCss from 'dibs-css';
import styles from '../styles/styles.scss';

import { type ProductTitle_item$key } from './__generated__/ProductTitle_item.graphql';

const itemFragment = graphql`
    fragment ProductTitle_item on Item {
        serviceId
        isArt
        title
        classification {
            creationDate
        }
        creators {
            creator {
                displayName
            }
        }
        linkData {
            ...SeoLink_linkData
        }
        ...useSwatches_item
    }
`;

const CREATOR_UNKNOWN = 'unknown';

type Props = {
    item: ProductTitle_item$key;
    isSponsored?: boolean;
    onClick?: OnClick;
    isSmall: boolean;
    canShowDimensions: boolean;
    showMskuSwatch?: boolean;
};

export const ProductTitle: FC<Props> = ({
    item: itemRef,
    isSponsored,
    onClick,
    isSmall,
    showMskuSwatch,
    canShowDimensions,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const { isArt, title, classification, creators, linkData } = item;
    const itemId = item?.serviceId || '';
    const creationDate = classification?.creationDate;
    const attributionCreatorName = creators?.[0]?.creator?.displayName;
    const showAttributionCreatorName =
        attributionCreatorName && attributionCreatorName.toLowerCase() !== CREATOR_UNKNOWN;
    const dataTn = `item-tile-title-anchor${isSponsored ? '-sponsored' : ''}`;
    const swatches = useSwatches({ item });
    const isTitleOneLine = // we are saving space by forcing a single-line-title for items with a lot of details
        (showMskuSwatch && swatches.length > 0) || (isSmall && canShowDimensions);
    const isArtWithCombinedTitle = isArt && !isTitleOneLine; // combined title is "creator name + title + year"

    return (
        <HeadingLevel>
            {Heading => (
                <SeoLink
                    dataTn={dataTn}
                    // data-pk attribute is used by dibs-toho (MG)
                    dataPk={itemId}
                    onClick={onClick}
                    className={classnames(styles.tileLink, dibsCss.minW0, dibsCss.wFull, {
                        // art item title elements have their own truncation logic in the component,
                        // so we don't need to truncate twice
                        [dibsCss.lineClamp1]: isTitleOneLine && !isArtWithCombinedTitle,
                        [dibsCss.lineClamp2]: !isTitleOneLine && !isArtWithCombinedTitle,
                    })}
                    linkData={linkData}
                >
                    <Heading
                        className={classnames(
                            styles.tileLinkHeading,
                            dibsCss.textBodySmallNormal,
                            dibsCss.textNoir,
                            dibsCss.wFull,
                            dibsCss.m0
                        )}
                    >
                        {isArtWithCombinedTitle ? (
                            <>
                                {showAttributionCreatorName && (
                                    <span className={dibsCss.flex}>
                                        <span
                                            className={dibsCss.truncate}
                                            data-tn="portfolio-row-creator-name"
                                        >
                                            {attributionCreatorName}
                                        </span>
                                    </span>
                                )}
                                <span
                                    className={classnames(
                                        dibsCss.flex,
                                        dibsCss.flexNowrap,
                                        dibsCss.whitespaceNowrap,
                                        dibsCss.wFull
                                    )}
                                >
                                    <span
                                        className={dibsCss.truncate}
                                        data-tn="portfolio-row-product-name"
                                    >
                                        {title}
                                    </span>
                                    {creationDate && `, ${creationDate}`}
                                </span>
                            </>
                        ) : (
                            title
                        )}
                    </Heading>
                </SeoLink>
            )}
        </HeadingLevel>
    );
};
